// The first call has to wake the CryptoPro MV3 service worker AND the local native
// messaging host, which routinely takes several seconds on a cold start. The previous
// 500ms cap resolved to `false`, so a slow-but-working setup was reported to the user as
// "плагин не настроен" — Clarity shows people bouncing to cryptopro.ru and succeeding on
// the second attempt. Time out generously, and reject rather than resolve `false` so a
// timeout stays distinguishable from a genuine "system is not set up" answer.
//
// The same budget also covers window.cryptoPro not existing yet: crypto-pro-wrapper.js sets
// it from a script that can still be loading/executing when this is called (every caller —
// SignForm's certificate button, DocumentSignAction, QualifiedSignatureLogin, the profile
// certificate widgets — races it). Without the wait below, destructuring window.cryptoPro
// throws a plain TypeError, which every caller's catch reports as "plugin missing" — a false
// diagnosis for someone whose CryptoPro is fine and just hasn't finished loading yet.
// Exported so a caller can gate its own UI on the same wait instead of re-deriving a budget.
export const SETUP_PROBE_TIMEOUT_MS = 10000;
const SCRIPT_POLL_INTERVAL_MS = 50;

export class CryptoProProbeTimeout extends Error {
    constructor() {
        super('Плагин КриптоПро не ответил вовремя');
        this.name = 'CryptoProProbeTimeout';
    }
}

const waitForCryptoProScript = async (deadline) => {
    while (!window.cryptoPro) {
        if (Date.now() >= deadline) {
            throw new CryptoProProbeTimeout();
        }
        await new Promise((resolve) => setTimeout(resolve, SCRIPT_POLL_INTERVAL_MS));
    }
};

export default async function () {
    const deadline = Date.now() + SETUP_PROBE_TIMEOUT_MS;
    let timer;

    try {
        await waitForCryptoProScript(deadline);
        const { isValidSystemSetup } = window.cryptoPro;

        return await Promise.race([
            isValidSystemSetup(),
            new Promise((_, reject) => {
                timer = setTimeout(() => reject(new CryptoProProbeTimeout()), Math.max(deadline - Date.now(), 0));
            })
        ]);
    } finally {
        clearTimeout(timer);
    }
}
